import React from "react";
import { Select, MenuItem, Box, InputLabel, FormControl, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { METRIC_UNITS, Question, Questionnaire, US_CUSTOMARY_UNITS } from "./models";
import RequiredAsterisk from "./RequiredAsterisk";

const INCH_TO_CM = 2.54;
const FOOT = 12;
const MIN_HEIGHT_CM = 51;
const MAX_HEIGHT_CM = 302;

const toFeetInches = (totalInches: number) => {
  const rawFeet = Math.floor(totalInches / FOOT);
  const fractionalInches = totalInches - rawFeet * FOOT;
  let roundedInches = Math.round(fractionalInches);
  let adjustedFeet = rawFeet;

  if (roundedInches >= FOOT) {
    adjustedFeet += Math.floor(roundedInches / FOOT);
    roundedInches = roundedInches % FOOT;
  }

  return { feet: adjustedFeet, inches: roundedInches };
};

const InputHeightView = ({ question, questionnaire, onChange }: { question: Question, questionnaire: Questionnaire, onChange: (Question: Question) => void }) => {
    const { t } = useTranslation(questionnaire.cohort);
    const labelText = t(question.question);
    const [cmError, setCmError] = React.useState<string>("");
    const [cmInput, setCmInput] = React.useState<string>("");

    const unit = question.unit || US_CUSTOMARY_UNITS;

    const hasStoredValue =
        question.value !== "" &&
        question.value !== null &&
        question.value !== undefined &&
        !isNaN(Number(question.value));

    const storedInches = hasStoredValue ? Number(question.value) : 0;
    const { feet, inches } = toFeetInches(storedInches);

    // Sync cmInput from question.value when unit is metric or unit changes
    React.useEffect(() => {
        if (unit !== METRIC_UNITS) return;

        if (!hasStoredValue) {
            setCmInput("");
            return;
        }

        setCmInput(Math.round(storedInches * INCH_TO_CM).toString());
    }, [unit, question.value]);

    const feetOptions = Array.from({ length: 10 }, (_, i) => i.toString()); // 0-9 feet
    const inchOptions = Array.from({ length: 12 }, (_, i) => i.toString()); // 0-11 inches

    const handleMetricChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setCmInput(input);

        if (input === "") {
            setCmError("");
            onChange({ ...question, value: "" });
            return;
        }

        const updatedCm = Number(input);
        if (!isNaN(updatedCm) && updatedCm >= MIN_HEIGHT_CM && updatedCm <= MAX_HEIGHT_CM) {
            setCmError("");
            // convert to inches
            const updatedValue = (updatedCm / INCH_TO_CM).toFixed(2);
            onChange({ ...question, value: updatedValue });
        } else {
            setCmError(`Please enter a value between ${MIN_HEIGHT_CM} and ${MAX_HEIGHT_CM} centimeters.`);
        }
    };

    const handleFeetChange = (event: SelectChangeEvent<string>) => {
        const updatedFeet = Number(event.target.value);
        // convert to inches
        const updatedValue = updatedFeet * FOOT + inches;
        onChange({ ...question, value: `${updatedValue}` });
    };

    const handleInchesChange = (event: SelectChangeEvent<string>) => {
        const updatedInches = Number(event.target.value);
        // convert to inches
        const updatedValue = feet * FOOT + updatedInches;
        onChange({ ...question, value: `${updatedValue}` });
    };

    return (
      <Box>
        <Box mb={2}>
          <Typography variant="h6" component='div'>
            {labelText}
            {question.required && <RequiredAsterisk />}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {unit === "1" ? (
          <TextField
              label={t("Centimeters (cm)")}
              type="text"
              value={cmInput}
              onChange={handleMetricChange}
              error={!!cmError}
              helperText={cmError}
              fullWidth
          />
          ) : (
          <>
            <FormControl style={{ width: 100 }}>
              <InputLabel id="feet-label" htmlFor="feet">{t("Feet")}</InputLabel>
              <Select
                  labelId="feet-label"
                  value={feet.toString()}
                  label={t("Feet")}
                  onChange={handleFeetChange}
                  inputProps={{ id: 'feet', style: { display: 'none' } }}
              >
                  {feetOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl style={{ width: 100 }}>
              <InputLabel id="inches-label" htmlFor="inches">{t("Inches")}</InputLabel>
              <Select
                  labelId="inches-label"
                  value={inches.toString()}
                  label={t("Inches")}
                  onChange={handleInchesChange}
                  inputProps={{ id: 'inches', style: { display: 'none' } }}
              >
                  {inchOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </>
          )}
        </Box>
      </Box>
    );
};

export default InputHeightView;
